// Inicio de sesion con las credenciales de Twenty y seleccion de empresa (workspace).
// Flujo: signIn(email, clave) -> lista de empresas con un loginToken cada una ->
// getAuthTokensFromLoginToken(loginToken, origin = url de la empresa) -> tokens de esa empresa.

import { ApiError, metadataQuery, metadataSesion, setSession, type Session } from './http';

export type Empresa = { id: string; nombre: string; url: string; logo?: string; loginToken?: string };

export async function signIn(email: string, password: string): Promise<Empresa[]> {
  const data = await metadataQuery<{
    signIn: { availableWorkspaces: { availableWorkspacesForSignIn: RawWs[]; availableWorkspacesForSignUp: RawWs[] } };
  }>(
    `mutation Entrar($email: String!, $password: String!) {
      signIn(email: $email, password: $password) {
        availableWorkspaces {
          availableWorkspacesForSignIn { id displayName logo loginToken workspaceUrls { subdomainUrl customUrl } }
          availableWorkspacesForSignUp { id displayName logo loginToken workspaceUrls { subdomainUrl customUrl } }
        }
      }
    }`,
    { email: email.trim(), password },
  );
  const lista = data.signIn.availableWorkspaces.availableWorkspacesForSignIn ?? [];
  return lista.map((w) => ({
    id: w.id,
    nombre: w.displayName || 'Empresa sin nombre',
    url: (w.workspaceUrls.customUrl || w.workspaceUrls.subdomainUrl).replace(/\/$/, ''),
    logo: w.logo || undefined,
    loginToken: w.loginToken || undefined,
  }));
}

type RawWs = {
  id: string;
  displayName?: string;
  logo?: string;
  loginToken?: string;
  workspaceUrls: { subdomainUrl: string; customUrl?: string };
};

export async function enterWorkspace(email: string, empresa: Empresa) {
  if (!empresa.loginToken) throw new Error('Esta empresa no permite entrar con contraseña. Pide acceso al administrador del CRM.');
  const data = await metadataQuery<{
    getAuthTokensFromLoginToken: { tokens: { accessOrWorkspaceAgnosticToken: { token: string; expiresAt: string }; refreshToken: { token: string } } };
  }>(
    `mutation Tokens($loginToken: String!, $origin: String!) {
      getAuthTokensFromLoginToken(loginToken: $loginToken, origin: $origin) {
        tokens { accessOrWorkspaceAgnosticToken { token expiresAt } refreshToken { token } }
      }
    }`,
    { loginToken: empresa.loginToken, origin: empresa.url },
  );
  const t = data.getAuthTokensFromLoginToken.tokens;
  const sesion: Session = {
    modo: 'usuario',
    accessToken: t.accessOrWorkspaceAgnosticToken.token,
    expiresAt: t.accessOrWorkspaceAgnosticToken.expiresAt,
    refreshToken: t.refreshToken.token,
    email,
    workspace: { id: empresa.id, nombre: empresa.nombre, url: empresa.url, logo: empresa.logo },
  };
  setSession(sesion);
  return sesion;
}

// Modo API key: para puestos fijos, pruebas automaticas o cuando se prefiere no usar contrasena.
export async function enterWithApiKey(apiKey: string, nombre?: string) {
  const token = apiKey.trim();
  // Se valida antes de abrir la sesion, para no entrar y salir de golpe si la key no sirve.
  let res: Response;
  try {
    res = await fetch('/rest/workspaceMembers?limit=1', { headers: { Authorization: `Bearer ${token}` } });
  } catch {
    throw new ApiError(0, 'No hay conexión con el servidor del CRM.');
  }
  if (!res.ok) throw new ApiError(res.status, res.status === 401 || res.status === 403 ? 'la key no es válida o fue revocada.' : `el servidor respondió ${res.status}.`);
  const sesion: Session = { modo: 'apikey', accessToken: token, workspace: { nombre: nombre || 'Empresa' } };
  setSession(sesion);
  return sesion;
}

export type CurrentUser = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  workspaceMember?: { id: string; name: { firstName: string; lastName: string }; roles?: { id: string; label: string }[] | null } | null;
  currentWorkspace?: { id: string; displayName?: string; logo?: string } | null;
  // Permisos del rol en esta empresa; WORKSPACE = puede administrarla (usuarios, llaves, configuracion).
  currentUserWorkspace?: { permissionFlags?: string[] | null } | null;
};

export async function currentUser() {
  const data = await metadataSesion<{ currentUser: CurrentUser }>(
    `query Yo { currentUser { id email firstName lastName workspaceMember { id name { firstName lastName } roles { id label } } currentWorkspace { id displayName logo } currentUserWorkspace { permissionFlags } } }`,
  );
  return data.currentUser;
}

export const signOut = () => setSession(null);
